import { FolderUp, Pause, Play, Volume2, VolumeX } from '@music-vine/cadence/icons';
import { Button, toast } from '@music-vine/cadence/ui';
import { AnimatePresence, motion } from 'motion/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { audioElementAtom } from '@/features/AudioPlayer/atoms';
import { clearVideoKeepOpenAtom, toggleVideoMuteAtom, uploadVideoFileAtom, videoFileAtom, videoFileMetadataAtom, videoLoadingAtom, videoMutedAtom, videoUploadErrorAtom } from '../atoms/videoPlayerAtoms';
import { useVideoPlayerRef } from '../hooks/useVideoPlayerRef';
import { VideoControls } from './VideoControls';
import { extractFileMetadata, trackVideoLoadError, trackVideoUploadAttempt, trackVideoUploadError, trackVideoUploadSuccess } from '../utils/tracking';

// Video file validation constants
const SUPPORTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime',
// .mov files
'video/x-msvideo' // .avi files
];

/**
 * Video element with playback controls and reset point UI
 */
export const VideoPlayer = () => {
  const {
    asPath
  } = useRouter();
  const videoRef = useVideoPlayerRef();
  const videoFile = useAtomValue(videoFileAtom);
  const fileMetadata = useAtomValue(videoFileMetadataAtom);
  const isMuted = useAtomValue(videoMutedAtom);
  const audioElement = useAtomValue(audioElementAtom);
  const toggleMute = useSetAtom(toggleVideoMuteAtom);
  const uploadVideo = useSetAtom(uploadVideoFileAtom);
  const clearVideoKeepOpen = useSetAtom(clearVideoKeepOpenAtom);
  const setUploadError = useSetAtom(videoUploadErrorAtom);
  const setLoading = useSetAtom(videoLoadingAtom);
  const [hasAudio, setHasAudio] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [playPauseFeedback, setPlayPauseFeedback] = useState<'play' | 'pause' | null>(null);
  const dragCounterRef = useRef(0);
  const feedbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasTrackedSuccessRef = useRef(false);

  // Cleanup feedback timeout on unmount
  useEffect(() => () => {
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }
  }, []);
  const checkVideoTrack = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const video = e?.currentTarget;

    // videoWidth/videoHeight will be 0 if video track is missing/corrupt
    if (video.readyState >= 1 && video.videoWidth === 0 && video.videoHeight === 0) {
      trackVideoLoadError(fileMetadata, asPath);
      setUploadError('Error playing video');
      setLoading(false);
      clearVideoKeepOpen();
    }
  };

  // Detect if video has audio tracks
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const checkAudioTracks = () => {
      // Use type assertion for browser-specific properties
      const videoElement = video as HTMLVideoElement & {
        mozHasAudio?: boolean;
        webkitAudioDecodedByteCount?: number;
        audioTracks?: {
          length: number;
        };
      };

      // Try multiple detection methods
      let hasAudioTracks = false;

      // Method 1: Check mozHasAudio (Firefox)
      if (videoElement.mozHasAudio === true) {
        hasAudioTracks = true;
      }
      // Method 2: Check webkitAudioDecodedByteCount (Chrome/Safari)
      // This property is only available after some video has been decoded
      else if (videoElement.webkitAudioDecodedByteCount !== undefined && videoElement.webkitAudioDecodedByteCount > 0) {
        hasAudioTracks = true;
      }
      // Method 3: Check audioTracks API
      else if (videoElement.audioTracks && videoElement.audioTracks.length > 0) {
        hasAudioTracks = true;
      }
      setHasAudio(hasAudioTracks);
    };
    video.addEventListener('loadedmetadata', checkAudioTracks);
    video.addEventListener('loadeddata', checkAudioTracks);
    video.addEventListener('canplay', checkAudioTracks);
    // Check periodically during playback for webkitAudioDecodedByteCount
    video.addEventListener('timeupdate', checkAudioTracks);

    // Check immediately if metadata is already loaded
    if (video.readyState >= 1) {
      checkAudioTracks();
    }
    return () => {
      video.removeEventListener('loadedmetadata', checkAudioTracks);
      video.removeEventListener('loadeddata', checkAudioTracks);
      video.removeEventListener('canplay', checkAudioTracks);
      video.removeEventListener('timeupdate', checkAudioTracks);
    };
  }, [videoRef, videoFile]);

  // Sync muted state to video element
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = isMuted;
    }
  }, [videoRef, isMuted]);

  // Reset tracking ref when video file changes
  useEffect(() => {
    hasTrackedSuccessRef.current = false;
  }, [videoFile]);

  // Auto-play video when a new file is loaded (only if audio is playing)
  useEffect(() => {
    const video = videoRef.current;
    if (!(video && videoFile && audioElement)) {
      return;
    }
    const handleCanPlay = () => {
      // Video loaded successfully - clear loading state
      setLoading(false);

      // Track success only once per video load
      if (!hasTrackedSuccessRef.current && fileMetadata) {
        hasTrackedSuccessRef.current = true;
        trackVideoUploadSuccess(fileMetadata, video, asPath);
      }

      // Only auto-play if audio is currently playing
      if (!audioElement.paused) {
        video.play().catch(error => {
          // Ignore autoplay errors - browser might block autoplay
          console.warn('Video autoplay blocked:', error);
        });
      }
    };
    const handleVideoError = () => {
      // Track load error with file metadata
      trackVideoLoadError(fileMetadata, asPath);

      // Set generic error, clear loading state, and clear video to return to upload screen (keep window open)
      setUploadError('Failed to load the video');
      setLoading(false);
      clearVideoKeepOpen();
    };
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleVideoError);

    // If video is already ready to play, trigger immediately
    if (video.readyState >= 3) {
      handleCanPlay();
    }
    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleVideoError);
    };
  }, [videoRef, videoFile, audioElement, fileMetadata, asPath]);

  // Handle video playback errors
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    // Wrap the play method to catch playback errors
    const originalPlay = video.play.bind(video);
    video.play = () => {
      const playPromise = originalPlay();
      playPromise.catch(error => {
        // Don't show errors for autoplay blocks (common and expected)
        if (error.name === 'NotAllowedError' || error.message?.includes('play() request was interrupted')) {
          return;
        }
        toast.error('Video playback failed');
      });
      return playPromise;
    };
    return () => {
      // Restore original play method on cleanup
      video.play = originalPlay;
    };
  }, [videoRef]);

  // Handle click on video to toggle play/pause for both video and audio
  const handleVideoClick = () => {
    const video = videoRef.current;
    if (!(video && audioElement)) {
      return;
    }

    // Clear any existing timeout
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }

    // If audio is playing, pause both
    if (audioElement.paused) {
      // If audio is paused, play both
      setPlayPauseFeedback('play');
      audioElement.play();
      video.play().catch(error => {
        console.warn('Video play blocked:', error);
      });
    } else {
      setPlayPauseFeedback('pause');
      audioElement.pause();
      video.pause();
    }

    // Hide feedback after animation
    feedbackTimeoutRef.current = setTimeout(() => {
      setPlayPauseFeedback(null);
    }, 500);
  };

  // Drag and drop handlers with counter to prevent flashing
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) {
      setIsDragging(true);
    }
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset counter and state
    dragCounterRef.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) {
      trackVideoUploadError('No file detected', null, asPath);
      toast.error('No file detected');
      return;
    }

    // Track upload attempt
    trackVideoUploadAttempt(file, asPath);
    const metadata = extractFileMetadata(file);

    // Validate file type - check if it's a video
    if (!file.type.startsWith('video/')) {
      trackVideoUploadError('Invalid file format', metadata, asPath);
      toast.error('Invalid file format');
      return;
    }

    // Validate specific video format
    if (!SUPPORTED_VIDEO_TYPES.includes(file.type)) {
      trackVideoUploadError('Invalid file format', metadata, asPath);
      toast.error('Invalid file format');
      return;
    }

    // All validations passed, upload the video
    try {
      uploadVideo(file);
    } catch {
      trackVideoUploadError('Failed to load the video', metadata, asPath);
      toast.error('Failed to load the video');
    }
  };
  return <div className="flex flex-col" onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop} data-sentry-component="VideoPlayer" data-sentry-source-file="VideoPlayer.tsx">
      {/* Video container with blurred background */}
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
        {/* Blurred background video */}
        <video aria-hidden="true" className="absolute inset-0 z-0 h-full w-full scale-110 object-cover opacity-60 blur-xs" preload="metadata" src={videoFile || undefined} tabIndex={-1} />

        {/* Main video element */}
        <video className="relative z-10 h-full w-full object-contain" controls={false} // Use custom controls only
      onLoadedMetadata={checkVideoTrack} preload="metadata" ref={videoRef} src={videoFile || undefined} />

        {/* Click overlay to toggle play/pause */}
        <button aria-label="Toggle play/pause" className="absolute inset-0 z-[15] cursor-pointer bg-transparent" onClick={handleVideoClick} type="button" />

        {/* Play/Pause feedback icon */}
        <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="VideoPlayer.tsx">
          {playPauseFeedback && <motion.div animate={{
          opacity: 1
        }} className="pointer-events-none absolute inset-0 z-[16] flex items-center justify-center" exit={{
          opacity: 0
        }} initial={{
          opacity: 0
        }} key={playPauseFeedback} transition={{
          duration: 0.15
        }}>
              <motion.div animate={{
            scale: [0.5, 1.1, 1]
          }} className="flex size-16 items-center justify-center rounded-full bg-black/50" initial={{
            scale: 0.5
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }}>
                {playPauseFeedback === 'play' ? <Play className="size-8 fill-white text-white" /> : <Pause className="size-8 fill-white text-white" />}
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Drag and drop overlay */}
        {/** biome-ignore lint/nursery/noLeakedRender: <Stupid rule no thank you> */}
        {isDragging && <div className="@container pointer-events-none absolute inset-0 z-40 flex items-center justify-center rounded-lg bg-white/55 backdrop-blur-xs dark:bg-gray-950/55">
            <div className="flex h-[70%] w-[80%] flex-col items-center justify-center gap-3 rounded-lg border-2 border-pink-500 border-dashed bg-pink-50 dark:bg-pink-950">
              <FolderUp className="@[300px]:size-8 @[400px]:size-10 size-6 fill-none text-pink-500" />
              <div className="text-center">
                <h3 className="font-sans @[300px]:text-sm @[400px]:text-base text-gray-700 text-xs dark:text-white">
                  <span className="font-semibold">Drop video</span> to replace
                </h3>
              </div>
            </div>
          </div>}

        {/* Gradient overlay and mute toggle (only shown if video has audio) */}
        {/** biome-ignore lint/nursery/noLeakedRender: <Stupid rule no thank you> */}
        {hasAudio && <>
            {/* Gradient overlay at bottom */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-8 bg-gradient-to-t from-black/60 to-transparent" />

            {/* Mute toggle button */}
            <div className="absolute right-3 @[386px]:bottom-4 bottom-2 z-30 h-4">
              <Button className="@[386px]:size-6 size-4 rounded-full bg-transparent hover:bg-transparent dark:hover:bg-transparent" icon onClick={toggleMute} size="sm" variant="transparent">
                {isMuted ? <VolumeX className="@[386px]:size-6 size-4 fill-none text-white [&>path:first-child]:fill-white" /> : <Volume2 className="@[386px]:size-6 size-4 fill-none text-white [&>path:first-child]:fill-white" />}
              </Button>
            </div>
          </>}
      </div>

      {/* Custom controls */}
      <VideoControls data-sentry-element="VideoControls" data-sentry-source-file="VideoPlayer.tsx" />
    </div>;
};